import type {ToolCallMessagePartComponent} from '@assistant-ui/react';
import {
  MOON_200,
  MOON_300,
  MOON_450,
  MOON_650,
  MOON_700,
  MOON_800,
  TEAL_500,
  WB_RUN_COLORS,
  WHITE,
} from '@wandb/weave/common/css/color.styles';
import type * as Plotly from 'plotly.js';
import React, {useEffect, useMemo, useRef, useState} from 'react';

import {useColorMode} from '../../../util/useColorMode';

export const PLOTLY_PANEL_TOOL_NAME = 'wbui__inchat_plotly_panel';
/** Kept only so persisted transcripts using the original name still render. */
export const LEGACY_PLOTLY_CHART_TOOL_NAME = 'wbui__plotly_chart';

type PlotlyPanelArgs = {
  data?: Plotly.Data[];
  layout?: Partial<Plotly.Layout>;
};

type PlotlyModule = typeof import('plotly.js');

const DEFAULT_CHART_HEIGHT = 360;
const DEFAULT_MARGIN: Partial<Plotly.Margin> = {t: 48, r: 24, b: 48, l: 56};
const RENDER_ERROR = 'This visualization could not be rendered.';
const PLOT_CONFIG: Partial<Plotly.Config> = {
  displaylogo: false,
  displayModeBar: false,
  responsive: true,
  scrollZoom: false,
};

export function buildPlotlyLayout(
  layout: Partial<Plotly.Layout> = {},
  isDarkMode = false
): Partial<Plotly.Layout> {
  const textColor = isDarkMode ? MOON_300 : MOON_650;
  const gridColor = isDarkMode ? MOON_700 : MOON_200;
  const axisColor = isDarkMode ? MOON_650 : MOON_300;

  const axisTheme: Partial<Plotly.LayoutAxis> = {
    automargin: true,
    gridcolor: gridColor,
    linecolor: axisColor,
    tickfont: {color: textColor},
    zerolinecolor: gridColor,
  };
  const theme: Partial<Plotly.Layout> = {
    autosize: true,
    height: DEFAULT_CHART_HEIGHT,
    colorway: WB_RUN_COLORS,
    paper_bgcolor: 'transparent',
    plot_bgcolor: 'transparent',
  };

  return {
    ...theme,
    ...layout,
    font: {color: textColor, ...layout.font},
    hoverlabel: {
      bgcolor: isDarkMode ? MOON_800 : WHITE,
      bordercolor: axisColor,
      font: {color: textColor},
      ...layout.hoverlabel,
    },
    margin: {...DEFAULT_MARGIN, ...layout.margin},
    modebar: {
      activecolor: TEAL_500,
      bgcolor: 'transparent',
      color: isDarkMode ? MOON_450 : MOON_650,
      ...layout.modebar,
    },
    xaxis: {...axisTheme, ...layout.xaxis},
    yaxis: {...axisTheme, ...layout.yaxis},
  };
}

/** Render a backend-owned Plotly UI action as a responsive transcript chart. */
export const PlotlyPanelToolUI: ToolCallMessagePartComponent<
  PlotlyPanelArgs
> = ({args}) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const [plotly, setPlotly] = useState<PlotlyModule | null>(null);
  const [renderError, setRenderError] = useState<string | null>(null);
  const {isDarkMode} = useColorMode();
  const data = useMemo(
    () => (Array.isArray(args?.data) ? args.data : []),
    [args?.data]
  );
  const figureLayout = useMemo(() => args?.layout ?? {}, [args?.layout]);
  const layout = useMemo(
    () => buildPlotlyLayout(figureLayout, isDarkMode),
    [figureLayout, isDarkMode]
  );

  useEffect(() => {
    let active = true;
    void import('plotly.js').then(module => {
      if (active) {
        setPlotly(module);
      }
    });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    const chart = chartRef.current;
    if (plotly == null || chart == null || data.length === 0) {
      return;
    }

    setRenderError(null);
    const validationError = plotly.validate(data, figureLayout)?.[0];
    if (validationError != null) {
      setRenderError(validationError.msg);
      return;
    }
    void plotly
      .react(chart, data, layout, PLOT_CONFIG)
      .catch(() => setRenderError(RENDER_ERROR));
  }, [data, figureLayout, layout, plotly]);

  useEffect(() => {
    const chart = chartRef.current;
    if (
      plotly == null ||
      chart == null ||
      typeof ResizeObserver === 'undefined'
    ) {
      return;
    }
    const observer = new ResizeObserver(() => {
      void plotly.Plots.resize(chart);
    });
    observer.observe(chart);
    return () => observer.disconnect();
  }, [data.length, plotly]);

  useEffect(() => {
    const chart = chartRef.current;
    return () => {
      if (plotly != null && chart != null) {
        plotly.purge(chart);
      }
    };
  }, [data.length, plotly]);

  return (
    <div className="my-8 overflow-hidden rounded-lg bg-wb-surface-raised">
      {data.length === 0 ? (
        <div className="flex h-96 items-center justify-center text-sm text-wb-text-tertiary">
          Preparing visualization…
        </div>
      ) : (
        <div ref={chartRef} className="min-h-[360px] w-full" />
      )}
      {renderError && (
        <div className="border-t border-wb-border-divider px-12 py-8 text-wb-error-text">
          {renderError}
        </div>
      )}
    </div>
  );
};
